import {
  ServiceEnvironmentOverride,
  ServiceSpec,
} from "src/domains/application/types";

import {
  ENV_KEYS,
  SERVICE_DEFAULTS,
  EditableService,
  EnvKey,
  EnvServiceValues,
} from "./editable-services-constants";

export function getDefaultsForType(type: string) {
  return SERVICE_DEFAULTS[type] || SERVICE_DEFAULTS.server;
}

// ── Helper: check if an env has overrides (differs from dev base) ────────────

export function envHasOverrides(
  service: EditableService,
  env: EnvKey,
): boolean {
  if (env === "dev") return false;
  const base = service.environments.dev;
  const target = service.environments[env];
  return (
    base.cpu !== target.cpu ||
    base.memory !== target.memory ||
    base.storage !== target.storage ||
    base.replicas !== target.replicas
  );
}

// ── Helper: build a fresh EditableService from a template ServiceSpec ────────

interface SavedService {
  name: string;
  type: string;
  cpu: number;
  memory: number;
  storage: number;
  replicas: number;
  internetFacing: boolean;
}

function buildEnvValues(
  cpu: number,
  memory: number,
  storage: number,
  replicas: number,
): Record<EnvKey, EnvServiceValues> {
  return {
    dev: { cpu, memory, storage, replicas },
    staging: { cpu, memory, storage, replicas },
    production: { cpu, memory, storage, replicas },
  };
}

/** Build the merged EditableService[] from template + saved stackConfig. */
export function buildMergedServices(
  templateServicesList: ServiceSpec[],
  savedServices: SavedService[],
  savedEnvs: Record<string, { services?: ServiceEnvironmentOverride[] }>,
): EditableService[] {
  return templateServicesList.map((s) => {
    const saved = savedServices.find((ss) => ss.name === s.name);
    const defaults = getDefaultsForType(s.type);

    const baseCpu =
      saved?.cpu ??
      (s.resources?.cpu ? parseInt(s.resources.cpu) / 1000 : defaults.cpu);
    const baseMemory =
      saved?.memory ??
      (s.resources?.memory
        ? parseInt(s.resources.memory) / 1024
        : defaults.memory);
    const baseStorage =
      saved?.storage ??
      (s.storage ? parseInt(s.storage) / 1024 : defaults.storage);
    const baseReplicas = saved?.replicas ?? 1;

    const enabledEnvs = new Set<EnvKey>(["dev", "staging", "production"]);
    const environments = buildEnvValues(
      baseCpu,
      baseMemory,
      baseStorage,
      baseReplicas,
    );

    // Apply saved overrides for each env
    for (const env of ENV_KEYS) {
      if (env === "dev") continue;
      const envConfig = savedEnvs[env];
      const envOverride = envConfig?.services?.find((ov) => ov.name === s.name);
      if (envOverride) {
        environments[env] = {
          cpu: envOverride.resources?.cpu
            ? parseInt(envOverride.resources.cpu) / 1000
            : baseCpu,
          memory: envOverride.resources?.memory
            ? parseInt(envOverride.resources.memory) / 1024
            : baseMemory,
          storage: baseStorage, // storage not in env overrides
          replicas: envOverride.replicas ?? baseReplicas,
        };
      }
    }

    return {
      name: s.name,
      type: s.type,
      internetFacing: saved?.internetFacing ?? s.internetFacing ?? false,
      environments,
      enabledEnvs,
    };
  });
}

/** Build a fresh EditableService from a single template ServiceSpec (add flow). */
export function buildServiceFromSpec(spec: ServiceSpec): EditableService {
  const defaults = getDefaultsForType(spec.type);
  const baseCpu = spec.resources?.cpu
    ? parseInt(spec.resources.cpu) / 1000
    : defaults.cpu;
  const baseMemory = spec.resources?.memory
    ? parseInt(spec.resources.memory) / 1024
    : defaults.memory;
  const baseStorage = spec.storage
    ? parseInt(spec.storage) / 1024
    : defaults.storage;

  return {
    name: spec.name,
    type: spec.type,
    internetFacing: spec.internetFacing ?? false,
    enabledEnvs: new Set<EnvKey>(["dev", "staging", "production"]),
    environments: buildEnvValues(baseCpu, baseMemory, baseStorage, 1),
  };
}

// ── Helper: build the save payload (base services + env overrides) ──────────

export interface SavePayload {
  services: {
    name: string;
    type: string;
    cpu: number;
    memory: number;
    storage: number;
    replicas: number;
    internetFacing: boolean;
  }[];
  environments: Record<string, { services: ServiceEnvironmentOverride[] }>;
}

/** Build the save payload: base services (from dev) + environment overrides. */
export function buildSavePayload(services: EditableService[]): SavePayload {
  const servicesPayload = services.map((svc) => ({
    name: svc.name,
    type: svc.type,
    cpu: svc.environments.dev.cpu,
    memory: svc.environments.dev.memory,
    storage: svc.environments.dev.storage,
    replicas: svc.environments.dev.replicas,
    internetFacing: svc.internetFacing,
  }));

  const environments: Record<
    string,
    { services: ServiceEnvironmentOverride[] }
  > = {};

  for (const env of ENV_KEYS) {
    if (env === "dev") continue;
    const overrides: ServiceEnvironmentOverride[] = [];

    for (const svc of services) {
      if (!svc.enabledEnvs.has(env)) continue;
      const base = svc.environments.dev;
      const target = svc.environments[env];
      const hasDiff =
        base.cpu !== target.cpu ||
        base.memory !== target.memory ||
        base.replicas !== target.replicas;

      if (hasDiff) {
        overrides.push({
          name: svc.name,
          replicas: target.replicas,
          resources: {
            cpu: `${Math.round(target.cpu * 1000)}m`,
            memory: `${Math.round(target.memory * 1024)}Mi`,
          },
        });
      }
    }

    if (overrides.length > 0) {
      environments[env] = { services: overrides };
    }
  }

  return { services: servicesPayload, environments };
}
